import { resolvedConcentrationDisplay } from './concentrationResolver.js'
import { normalizeSearch } from './searchAbbrev.js'

const CONCENTRATION_ORDER = [
  'cologne', 'edc', 'eau de cologne', 'edt', 'eau de toilette', 'edp',
  'eau de parfum', 'edp intense', 'eau de parfum intense', 'intense', 'le parfum', 'parfum',
  'extrait', 'extrait de parfum', 'elixir', 'perfume oil',
]

const LINE_SUFFIXES = [
  'eau de cologne', 'eau de toilette', 'eau de parfum', 'extrait de parfum',
  'edc', 'edt', 'edp', 'edp intense', 'le parfum', 'parfum', 'extrait',
  'elixir',
].sort((a, b) => b.length - a.length)

const ROMANCE_ARTICLES = new Set(['l', 'le', 'la', 'les'])
const STEM_CONNECTORS = new Set([
  'a', 'au', 'aux', 'by', 'd', 'de', 'des', 'di', 'du', 'for', 'in', 'of',
  'pour', 'the',
])

export const MIN_HOUSE_QUERY_LENGTH = 3
export const ALPHABETICAL_LINE_GROUP_THRESHOLD = 40

function clean(value) {
  return String(value || '').trim().replace(/\s+/g, ' ')
}

function normalized(value) {
  return clean(value).toLocaleLowerCase()
}

function normalizedToken(value) {
  return normalized(value).replace(/[^\p{L}\p{N}]+/gu, '')
}

function concentrationRank(value) {
  const index = CONCENTRATION_ORDER.indexOf(normalized(value))
  return index === -1 ? CONCENTRATION_ORDER.length : index
}

function preferRepresentative(current, candidate) {
  const currentHasImage = Boolean(current.card_image) && current.image_flagged !== true
  const candidateHasImage = Boolean(candidate.card_image) && candidate.image_flagged !== true
  if (currentHasImage !== candidateHasImage) {
    return candidateHasImage ? candidate : current
  }
  return String(candidate.id).localeCompare(String(current.id)) < 0 ? candidate : current
}

function browseLetter(value) {
  const first = clean(value).normalize('NFD').replace(/\p{Diacritic}/gu, '').charAt(0).toLocaleUpperCase()
  return /^[A-Z]$/.test(first) ? first : '#'
}

export function groupLinesForBrowse(lines = [], totalLineCount = lines.length) {
  if (totalLineCount <= ALPHABETICAL_LINE_GROUP_THRESHOLD) {
    return [{ letter: '', lines }]
  }

  const groups = new Map()
  for (const line of lines) {
    const letter = browseLetter(line?.name)
    if (!groups.has(letter)) groups.set(letter, [])
    groups.get(letter).push(line)
  }
  return [...groups].map(([letter, groupedLines]) => ({ letter, lines: groupedLines }))
}

function stripLeadingHouse(name, house) {
  const cleanName = clean(name)
  const cleanHouse = clean(house)
  if (!cleanHouse || normalized(cleanName) === normalized(cleanHouse)) return cleanName
  if (!normalized(cleanName).startsWith(`${normalized(cleanHouse)} `)) return cleanName
  return clean(cleanName.slice(cleanHouse.length))
}

function stripConcentrationSuffix(name, row) {
  const lower = normalized(name)
  const resolved = normalized(resolvedConcentrationDisplay(row))
  const suffixes = [resolved, ...LINE_SUFFIXES].filter(Boolean)

  for (const suffix of suffixes) {
    if (lower === suffix || !lower.endsWith(` ${suffix}`)) continue
    const candidate = clean(name.slice(0, name.length - suffix.length))
    if (candidate.length >= 2) return candidate
  }
  return name
}

// The catalog has no authoritative line/family column. Build a conservative
// display stem from the catalog name: remove a repeated house prefix and a
// terminal concentration, then keep the first two meaningful words. Romance
// articles (Le/La/L') stay attached to the first meaningful word, while "The"
// keeps two words so "The Most Wanted" remains intelligible before clustering.
export function deriveLineName(row) {
  const withoutHouse = stripLeadingHouse(row?.name, row?.house)
  const name = stripConcentrationSuffix(withoutHouse, row)
  const words = clean(name).split(' ').filter(Boolean)
  if (words.length <= 1) return clean(name)

  const first = normalizedToken(words[0])
  const meaningfulTarget = ROMANCE_ARTICLES.has(first) ? 1 : 2
  let meaningful = 0
  let end = words.length
  for (let index = 0; index < words.length; index += 1) {
    const token = normalizedToken(words[index])
    if (!STEM_CONNECTORS.has(token) && !ROMANCE_ARTICLES.has(token)) meaningful += 1
    if (meaningful >= meaningfulTarget) {
      end = index + 1
      break
    }
  }
  return clean(words.slice(0, end).join(' '))
}

function mergeSingleWordFamilyAnchors(lines) {
  const anchors = [...lines.values()]
    .filter(line => normalized(line.name).split(' ').length === 1 && normalized(line.name).length >= 5)
    .sort((a, b) => normalized(b.name).length - normalized(a.name).length)

  for (const anchor of anchors) {
    const anchorKey = normalized(anchor.name)
    for (const [lineKey, line] of [...lines.entries()]) {
      if (line === anchor) continue
      const words = normalized(line.name).split(' ')
      if (words[0] !== anchorKey && words.at(-1) !== anchorKey) continue
      anchor.variants.push(...line.variants)
      lines.delete(lineKey)
    }
  }
}

export function buildHouseHierarchy(rows = []) {
  const houses = new Map()
  for (const row of rows) {
    if (!row?.id || !clean(row.house) || !clean(row.name)) continue
    const houseKey = normalized(row.house)
    if (!houses.has(houseKey)) {
      houses.set(houseKey, { house: clean(row.house), lines: new Map(), row_count: 0 })
    }
    const house = houses.get(houseKey)
    const lineName = deriveLineName(row)
    const lineKey = normalized(lineName)
    if (!house.lines.has(lineKey)) {
      house.lines.set(lineKey, { name: lineName, house: house.house, variants: [] })
    }
    const line = house.lines.get(lineKey)
    const identityKey = `${normalized(row.name)}\u0000${normalized(resolvedConcentrationDisplay(row))}`
    const duplicateIndex = line.variants.findIndex(variant => variant._identity_key === identityKey)
    const candidate = { ...row, _identity_key: identityKey, catalog_duplicate_count: 1 }
    if (duplicateIndex === -1) {
      line.variants.push(candidate)
    } else {
      const current = line.variants[duplicateIndex]
      const representative = preferRepresentative(current, candidate)
      line.variants[duplicateIndex] = {
        ...representative,
        _identity_key: identityKey,
        catalog_duplicate_count: (current.catalog_duplicate_count || 1) + 1,
      }
    }
    house.row_count += 1
  }

  for (const house of houses.values()) mergeSingleWordFamilyAnchors(house.lines)

  return [...houses.values()]
    .map(house => ({
      house: house.house,
      row_count: house.row_count,
      lines: [...house.lines.values()]
        .map(line => ({
          ...line,
          variants: line.variants.sort((a, b) => {
            const rank = concentrationRank(resolvedConcentrationDisplay(a)) - concentrationRank(resolvedConcentrationDisplay(b))
            return rank || clean(a.name).localeCompare(clean(b.name)) || String(a.id).localeCompare(String(b.id))
          }).map(({ _identity_key, ...variant }) => variant),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.house.localeCompare(b.house))
}

export function houseCandidates(rows = []) {
  const counts = new Map()
  for (const row of rows) {
    const house = clean(row?.house)
    if (!house) continue
    const key = normalized(house)
    const current = counts.get(key) || { house, row_count: 0 }
    current.row_count += 1
    counts.set(key, current)
  }
  return [...counts.values()].sort((a, b) => a.house.localeCompare(b.house))
}

// Level 1 deliberately queries the shared catalog rather than materializing a
// per-user hierarchy. The three-character house ILIKE is served by catalog_house_trgm;
// only matching wearable rows cross the wire, yielding exact counts per house.
export async function fetchCatalogHouses(client, query, { pageSize = 500, maxRows = 3000 } = {}) {
  const q = normalizeSearch(query)
  if (q.length < MIN_HOUSE_QUERY_LENGTH) return []
  const rows = []

  for (let from = 0; ; from += pageSize) {
    const { data, error } = await client
      .from('catalog')
      .select('id,house')
      .eq('product_class', 'wearable_perfume')
      .ilike('house', `%${q}%`)
      .order('house', { ascending: true })
      .range(from, from + pageSize - 1)
    if (error) throw error
    const page = data || []
    rows.push(...page)
    if (page.length < pageSize) break
    if (rows.length >= maxRows) throw new Error('Too many houses match. Add another letter.')
  }

  return houseCandidates(rows)
}

export async function fetchCompleteHouseCatalog(client, house, { pageSize = 200 } = {}) {
  const selectedHouse = clean(house)
  if (!selectedHouse) throw new Error('Choose a house first.')
  const rows = []
  let resolverFieldsAvailable = true

  for (let from = 0; ; from += pageSize) {
    const select = resolverFieldsAvailable
      ? 'id,name,house,product_class,concentration,concentration_display,concentration_normalized,concentration_resolution_status,concentration_confidence,concentration_conflict,concentration_alternate_values,concentration_review_required,concentration_evidence_sources,year,card_image'
      : 'id,name,house,product_class,concentration,year,card_image'
    let response = await client
      .from('catalog')
      .select(select)
      .eq('product_class', 'wearable_perfume')
      .eq('house', selectedHouse)
      .order('name', { ascending: true })
      .order('concentration', { ascending: true, nullsFirst: false })
      .range(from, from + pageSize - 1)
    if (response.error?.code === '42703' && resolverFieldsAvailable) {
      resolverFieldsAvailable = false
      response = await client
        .from('catalog')
        .select('id,name,house,product_class,concentration,year,card_image')
        .eq('product_class', 'wearable_perfume')
        .eq('house', selectedHouse)
        .order('name', { ascending: true })
        .order('concentration', { ascending: true, nullsFirst: false })
        .range(from, from + pageSize - 1)
    }
    const { data, error } = response
    if (error) throw error
    const page = data || []
    rows.push(...page)
    if (page.length < pageSize) break
    if (rows.length >= 2000) throw new Error('This house is too large to load safely.')
  }

  const suppressed = new Set()
  for (let from = 0; from < rows.length; from += 200) {
    const ids = rows.slice(from, from + 200).map(row => row.id)
    const { data, error } = await client.from('image_qa_suppress').select('catalog_id').in('catalog_id', ids)
    // Image suppression fails closed: an unreadable guard means every image in
    // that page is hidden, while identity navigation remains available.
    if (error) ids.forEach(id => suppressed.add(id))
    else (data || []).forEach(item => suppressed.add(item.catalog_id))
  }

  return rows.map(row => suppressed.has(row.id) ? { ...row, card_image: null, image_flagged: true } : row)
}
